import { PRO_BUYER_SEARCH_FILTER_PARAM } from "@/features/pro-buyer-price/static/pro-buyer-price.static";
import {
  type Facet,
  isChoiceFacet,
  isTermsFacet,
} from "@/features/search/api/types";
import { US_SHIPPABLE_SEARCH_PARAM } from "@/features/us-market/us-shippable";

/**
 * Facets the service returns that are never a generic filter here. WHO-5947
 * F14: `merchant_trade_discount_enabled` showed as an untranslated
 * "Merchant trade discount enabled" list. WHO-5953: its count only feeds the
 * Pro Buyer strip, which replaced the old "Only Pro Buyer prices" toggle.
 */
const HIDDEN_FACET_KEYS: ReadonlySet<string> = new Set([
  PRO_BUYER_SEARCH_FILTER_PARAM,
]);

/**
 * WHO-6510: on `/en-us` the "Ships to the US" filter is the page's default and has its own chip
 * (`UsShippableChip`), which is also the only way to switch it off. A generic chip or checkbox for
 * it would toggle a URL param the US market ignores.
 */
const US_MARKET_HIDDEN_FACET_KEYS: ReadonlySet<string> = new Set([
  ...HIDDEN_FACET_KEYS,
  US_SHIPPABLE_SEARCH_PARAM,
]);

/** Whether a facet has anything the user could actually apply. */
const hasApplicableOptions = (facet: Facet) => {
  if (isTermsFacet(facet)) {
    return facet.values.some(value => value.count > 0 || value.selected);
  }

  /* The service already drops the options its own range can't reach, so an empty list is the
   * whole test — there are no stats here to second-guess it with. */
  if (isChoiceFacet(facet)) {
    return facet.values.length > 0;
  }

  /* A range whose bounds are set stays, however degenerate its stats: filtering into a dead end
   * leaves no stats at all, and dropping the control would take the only way to clear it with it.
   * Terms and choices keep their applied value for the same reason (the service returns it counted
   * zero), which their `selected` checks above already cover. */
  if (facet.selected) {
    return true;
  }

  const { min, max } = facet.stats ?? {};

  return min != null && max != null && min !== max;
};

/**
 * The facets worth rendering, in the order the API returned them — which facets
 * exist and how they are ordered is the service's call, not ours. All we drop is
 * a facet with nothing selectable in it (empty terms, a degenerate range), and
 * the Pro Buyer count (WHO-5953), which only feeds the Pro Buyer strip: never a
 * filter control or an active-filter chip.
 *
 * Single source of truth so the filter panel and the active-filter chips can
 * never disagree about what is visible. `usMarket` (WHO-6510) also drops the
 * US shipping facet, which has its own chip there; see `useVisibleFacets`.
 */
export const getVisibleFacets = (facets: Facet[], usMarket = false) => {
  const hidden = usMarket ? US_MARKET_HIDDEN_FACET_KEYS : HIDDEN_FACET_KEYS;

  return facets.filter(
    facet => !hidden.has(facet.key) && hasApplicableOptions(facet)
  );
};

/**
 * Applied filters on one facet: one per selected term value, one per set range or choice —
 * counting only what the user could actually take off again.
 *
 * A landing page's own filters come back `selected` exactly like the user's, and the service marks
 * them `locked` because it is the only party that knows: the page recipe and the query string meet
 * in its merge, and nothing downstream can tell them apart afterwards. The web used to reconstruct
 * this by diffing the response against the URL, which meant reimplementing the merge rules — and
 * drifting from them (WHO-5064, WHO-5324).
 */
export const getFacetSelectedCount = (facet: Facet) => {
  if (isTermsFacet(facet)) {
    return facet.values.filter(value => value.selected && !value.locked).length;
  }

  if (isChoiceFacet(facet)) {
    return facet.values.some(value => value.selected && !value.locked) ? 1 : 0;
  }

  const selected = facet.selected;

  return selected && !selected.locked ? 1 : 0;
};

/**
 * Whether a term value is the landing page's rather than the user's.
 *
 * For callers that render something other than the facet control and so have no value object to
 * read the flag off — the taxonomy widget, which asks about the category its tree is anchored on.
 */
export const isTermLocked = (
  facets: Facet[],
  facetKey: string,
  value: string
) =>
  facets.some(
    facet =>
      facet.key === facetKey &&
      isTermsFacet(facet) &&
      facet.values.some(term => term.value === value && term.locked)
  );

/** Anything applied at all, the page's filters included — what makes a section worth opening. */
export const hasAnySelection = (facet: Facet) =>
  isTermsFacet(facet) || isChoiceFacet(facet)
    ? facet.values.some(value => value.selected)
    : facet.selected != null;

/**
 * Total applied filters across facets. Counts the same filters `ActiveFilters` gives a chip to, so
 * the mobile button badge and the chip row can't disagree.
 */
export const getAppliedFilterCount = (facets: Facet[]) =>
  facets.reduce((total, facet) => total + getFacetSelectedCount(facet), 0);
